import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Megaphone, RefreshCw, Loader2, Download, Sheet, FileSpreadsheet, FileText, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField, fmtDate, iso, presetRange } from "@/pages/MetaBilling";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => (n || 0).toLocaleString("en-IN");
const dash = (v, f) => (v === null || v === undefined || v === "" ? "—" : f(v));
const day = (s) => (s ? fmtDate(s.slice(0, 10)) : "");

const PRESETS = [["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["this", "This month"], ["last", "Last month"], ["year", "This year"]];
function adsRange(key) {
  const now = new Date();
  if (key === "90") { const f = new Date(now); f.setDate(f.getDate() - 89); return [iso(f), iso(now)]; }
  if (key === "year") return [iso(new Date(now.getFullYear(), 0, 1)), iso(now)];
  return presetRange(key);
}

const STATUS_STYLE = {
  ACTIVE: "text-green-400 bg-green-500/10", PAUSED: "text-slate-300 bg-white/10", CAMPAIGN_PAUSED: "text-slate-300 bg-white/10",
  ADSET_PAUSED: "text-slate-300 bg-white/10", ARCHIVED: "text-slate-400 bg-white/5", DELETED: "text-slate-400 bg-white/5",
  WITH_ISSUES: "text-red-400 bg-red-500/10", DISAPPROVED: "text-red-400 bg-red-500/10", PENDING_REVIEW: "text-amber-400 bg-amber-500/10",
};
const statusLabel = (s) => (s ? s.toLowerCase().replace(/_/g, " ") : "—");

const TABS = [
  ["adsets", "Ad sets"], ["ads", "Ads"], ["age_gender", "Age and gender"],
  ["placements", "Where it showed"], ["devices", "Devices"], ["regions", "Areas"],
];

function downloadPng(d, label) {
  const t = d.total;
  const camps = d.campaigns.slice(0, 8);
  const W = 1080, P = 70, rowH = 64;
  const H = 640 + camps.length * rowH + 110;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d");
  const font = (s, w = 400) => `${w} ${s}px "Segoe UI", Roboto, Arial, sans-serif`;
  x.fillStyle = "#fff"; x.fillRect(0, 0, W, H);
  x.fillStyle = "#ea580c"; x.fillRect(0, 0, W, 16);
  x.fillStyle = "#0f172a"; x.font = font(44, 800);
  x.fillText("Adiyash Gym — Meta Ads", P, 100);
  x.fillStyle = "#64748b"; x.font = font(30);
  x.fillText(label, P, 148);
  const tiles = [
    ["AMOUNT SPENT", inr(t.spend)], ["LEADS", num(t.leads)], ["COST PER LEAD", dash(t.cost_per_lead, inr)],
    ["VIEWS", num(t.impressions)], ["PEOPLE REACHED", num(t.reach)], ["CLICKS", num(t.clicks)],
  ];
  tiles.forEach(([k, v], i) => {
    const cx = P + (i % 3) * 320, cy = 230 + Math.floor(i / 3) * 170;
    x.fillStyle = "#94a3b8"; x.font = font(22, 700); x.fillText(k, cx, cy);
    x.fillStyle = i === 0 ? "#ea580c" : "#0f172a"; x.font = font(50, 800); x.fillText(v, cx, cy + 62);
  });
  let y = 600;
  x.fillStyle = "#94a3b8"; x.font = font(22, 700);
  x.fillText("CAMPAIGN", P, y); x.textAlign = "right"; x.fillText("SPENT  ·  LEADS", W - P, y); x.textAlign = "left";
  y += 20;
  camps.forEach((c) => {
    x.strokeStyle = "#e2e8f0"; x.lineWidth = 2;
    x.beginPath(); x.moveTo(P, y); x.lineTo(W - P, y); x.stroke();
    x.fillStyle = "#0f172a"; x.font = font(28, 700);
    x.fillText((c.name || "").slice(0, 34), P, y + 42);
    x.textAlign = "right"; x.fillText(`${inr(c.spend)}  ·  ${num(c.leads)}`, W - P, y + 42); x.textAlign = "left";
    y += rowH;
  });
  x.fillStyle = "#94a3b8"; x.font = font(22);
  x.fillText("From Meta Ads Manager data, before 18% GST.", P, H - 50);
  cv.toBlob((blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Meta Ads ${label}.png`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, "image/png");
}

export default function MetaAds() {
  const [range, setRange] = useState(() => adsRange("30"));
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("adsets");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState("");
  const [from, to] = range;

  const load = useCallback(async (refresh = false) => {
    if (!from || !to) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/meta-ads", { params: { start: from, end: to, ...(refresh ? { refresh: true } : {}) } });
      setD(res.data);
    } catch (e) {
      setD(null);
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const label = `${fmtDate(from)} – ${fmtDate(to)}`;

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get("/meta-ads/export", { params: { start: from, end: to, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Meta Ads ${label}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(""); }
  };

  const btn = "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-3 py-2 text-sm transition-all duration-150 active:scale-[0.97] flex items-center gap-2 disabled:opacity-50";
  const card = "bg-[#141414] border border-white/10 rounded-lg p-4";
  const cap = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";
  const exportBtn = "flex items-center gap-3 border border-white/10 rounded-md px-4 py-3 hover:bg-white/5 transition-all duration-150 disabled:opacity-60";

  const t = d?.total;
  const kpis = t ? [
    ["Amount spent", inr(t.spend), "text-orange-500"],
    ["Leads", num(t.leads), "text-white"],
    ["Cost per lead", dash(t.cost_per_lead, inr), "text-white"],
    ["Views (impressions)", num(t.impressions), "text-white"],
    ["People reached", num(t.reach), "text-white"],
    ["Clicks", num(t.clicks), "text-white"],
    ["Link clicks", num(t.link_clicks), "text-white"],
    ["Click rate (CTR)", `${t.ctr}%`, "text-white"],
    ["Cost per click", inr(t.cpc), "text-white"],
    ["Cost per 1,000 views", inr(t.cpm), "text-white"],
    ["Times each person saw it", String(t.frequency), "text-white"],
    ["Video views", num(t.video_views), "text-white"],
    ["WhatsApp chats started", num(t.conversations), "text-white"],
  ] : [];
  const maxDay = d ? Math.max(1, ...d.daily.map((x) => x.spend)) : 1;
  const tabRows = d ? d[tab] || [] : [];
  const showToken = d && d.token_days_left !== null && d.token_days_left <= 10;

  return (
    <div data-testid="meta-ads-page">
      <h1 className="font-heading text-4xl tracking-tight font-black text-white">Meta Ads</h1>
      <p className="text-sm text-slate-500 mt-1">How the Facebook and Instagram ads are doing: money spent, leads and views.</p>

      {showToken && (
        <div className="mt-4 flex items-start gap-2 text-sm rounded-md p-3 border bg-amber-500/10 border-amber-500/30 text-amber-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{d.token_days_left < 0 ? "The Meta ads token has expired." : `The Meta ads token expires in ${d.token_days_left} day(s) (${d.token_expires}).`} Make a new one and update <b>META_ADS_TOKEN</b> and <b>META_ADS_TOKEN_EXPIRES</b> on Railway.</span>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <DateField label="From" value={from} maxDate={to} onChange={(v) => setRange([v, to])} />
        <DateField label="To" value={to} minDate={from} maxDate={iso(new Date())} onChange={(v) => setRange([from, v])} />
        <button className={btn} onClick={() => load(true)} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
        {d && <button className={btn} onClick={() => setExportOpen(true)}><Download className="h-4 w-4" /> Export</button>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {PRESETS.map(([k, l]) => (
          <button key={k} className="text-xs font-semibold rounded-full px-3 py-1 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10" onClick={() => setRange(adsRange(k))}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-2">Up to 1 year at a time.</p>

      {error && <div className="mt-6 text-sm rounded-md p-3 border bg-red-500/10 border-red-500/30 text-red-400 break-words">Meta error: {error}</div>}
      {loading && !d && <p className="mt-8 text-slate-500 text-sm">Loading from Meta…</p>}

      {d && (
        <div className="mt-6 space-y-6 max-w-6xl">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {kpis.map(([k, v, cls]) => (
              <div key={k} className={card}>
                <p className={cap}>{k}</p>
                <p className={`text-2xl font-black mt-1 ${cls}`}>{v}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Account: <span className="text-slate-300">{d.account.name}</span> · Spent since the account started {dash(d.account.lifetime_spent, inr)} · Balance due {dash(d.account.balance, inr)} · Amounts before 18% GST
          </p>

          <div className={card}>
            <p className={`${cap} mb-3`}>Day by day (spend)</p>
            {d.daily.length === 0 ? <p className="text-sm text-slate-500">No ads ran in this period.</p> : (
              <>
                <div className="flex items-end gap-1 h-32">
                  {d.daily.map((x) => (
                    <div key={x.date} className="flex-1 flex flex-col justify-end h-full group" title={`${fmtDate(x.date)}: ${inr(x.spend)} · ${x.leads} leads · ${num(x.impressions)} views`}>
                      <div className="bg-orange-500/80 group-hover:bg-orange-400 rounded-t" style={{ height: `${Math.max(2, (x.spend / maxDay) * 100)}%` }} />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                  <span>{fmtDate(d.daily[0].date)}</span>
                  <span>Peak {inr(maxDay)}/day</span>
                  <span>{fmtDate(d.daily[d.daily.length - 1].date)}</span>
                </div>
              </>
            )}
          </div>

          <div className={card}>
            <p className={`${cap} mb-3 flex items-center gap-1`}><Megaphone className="h-3 w-3" /> Campaigns</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="py-2 pr-4 font-semibold">Campaign</th>
                    <th className="py-2 pr-4 font-semibold">Status</th>
                    <th className="py-2 pr-4 font-semibold">Starts</th>
                    <th className="py-2 pr-4 font-semibold">Ends</th>
                    <th className="py-2 pr-4 font-semibold text-right">Daily budget</th>
                    <th className="py-2 pr-4 font-semibold text-right">Spent</th>
                    <th className="py-2 pr-4 font-semibold text-right">Leads</th>
                    <th className="py-2 pr-4 font-semibold text-right">Per lead</th>
                    <th className="py-2 pr-4 font-semibold text-right">Views</th>
                    <th className="py-2 pr-4 font-semibold text-right">Reached</th>
                    <th className="py-2 pr-4 font-semibold text-right">Clicks</th>
                    <th className="py-2 font-semibold text-right">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {d.campaigns.length === 0 && <tr><td colSpan={12} className="py-4 text-slate-500">No campaigns in this period.</td></tr>}
                  {d.campaigns.map((c) => (
                    <tr key={c.id} className="border-t border-white/10 text-slate-300">
                      <td className="py-2.5 pr-4 font-bold text-white">{c.name}</td>
                      <td className="py-2.5 pr-4"><span className={`text-[10px] uppercase font-bold rounded px-2 py-0.5 ${STATUS_STYLE[c.status] || "text-amber-400 bg-amber-500/10"}`}>{statusLabel(c.status)}</span></td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{day(c.start_time) || "—"}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{day(c.stop_time) || "No end date"}</td>
                      <td className="py-2.5 pr-4 text-right">{dash(c.daily_budget, inr)}</td>
                      <td className="py-2.5 pr-4 text-right font-bold text-orange-500">{inr(c.spend)}</td>
                      <td className="py-2.5 pr-4 text-right font-bold text-white">{num(c.leads)}</td>
                      <td className="py-2.5 pr-4 text-right">{dash(c.cost_per_lead, inr)}</td>
                      <td className="py-2.5 pr-4 text-right">{num(c.impressions)}</td>
                      <td className="py-2.5 pr-4 text-right">{num(c.reach)}</td>
                      <td className="py-2.5 pr-4 text-right">{num(c.clicks)}</td>
                      <td className="py-2.5 text-right">{c.ctr}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={card}>
            <div className="flex flex-wrap gap-2 mb-3">
              {TABS.map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} className={`text-xs font-semibold rounded-full px-3 py-1 border ${tab === k ? "bg-orange-600 text-white border-orange-600" : "bg-white/5 hover:bg-white/10 text-slate-300 border-white/10"}`}>{l}</button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="py-2 pr-4 font-semibold">{tab === "adsets" || tab === "ads" ? "Name" : "Group"}</th>
                    {(tab === "adsets" || tab === "ads") && <th className="py-2 pr-4 font-semibold">Campaign</th>}
                    <th className="py-2 pr-4 font-semibold text-right">Spent</th>
                    <th className="py-2 pr-4 font-semibold text-right">Leads</th>
                    <th className="py-2 pr-4 font-semibold text-right">Per lead</th>
                    <th className="py-2 pr-4 font-semibold text-right">Views</th>
                    <th className="py-2 pr-4 font-semibold text-right">Reached</th>
                    <th className="py-2 pr-4 font-semibold text-right">Clicks</th>
                    <th className="py-2 font-semibold text-right">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {tabRows.length === 0 && <tr><td colSpan={9} className="py-4 text-slate-500">No data in this period.</td></tr>}
                  {tabRows.map((r, i) => (
                    <tr key={`${r.name || r.label}-${i}`} className="border-t border-white/10 text-slate-300">
                      <td className="py-2.5 pr-4 font-bold text-white">{r.name || r.label}</td>
                      {(tab === "adsets" || tab === "ads") && <td className="py-2.5 pr-4">{r.campaign}</td>}
                      <td className="py-2.5 pr-4 text-right font-bold text-orange-500">{inr(r.spend)}</td>
                      <td className="py-2.5 pr-4 text-right font-bold text-white">{num(r.leads)}</td>
                      <td className="py-2.5 pr-4 text-right">{dash(r.cost_per_lead, inr)}</td>
                      <td className="py-2.5 pr-4 text-right">{num(r.impressions)}</td>
                      <td className="py-2.5 pr-4 text-right">{num(r.reach)}</td>
                      <td className="py-2.5 pr-4 text-right">{num(r.clicks)}</td>
                      <td className="py-2.5 text-right">{r.ctr}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-slate-500">Numbers come straight from Meta and can change slightly for a day or two as Meta finishes counting. Refreshed at most every 10 minutes. Time zone: {d.account.timezone || "account time zone"}.</p>
        </div>
      )}

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent data-testid="meta-ads-export-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Export Meta Ads report</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-2 py-2">
            <button onClick={() => doExport("xlsx")} disabled={!!exporting} className={exportBtn}>
              {exporting === "xlsx" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sheet className="h-5 w-5 text-green-600" />}
              <span className="font-semibold text-sm">Excel (.xlsx) — all tables</span>
            </button>
            <button onClick={() => doExport("csv")} disabled={!!exporting} className={exportBtn}>
              {exporting === "csv" ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileSpreadsheet className="h-5 w-5 text-slate-300" />}
              <span className="font-semibold text-sm">CSV (.csv) — campaigns</span>
            </button>
            <button onClick={() => doExport("pdf")} disabled={!!exporting} className={exportBtn}>
              {exporting === "pdf" ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5 text-red-600" />}
              <span className="font-semibold text-sm">PDF (.pdf)</span>
            </button>
            <button onClick={() => downloadPng(d, label)} disabled={!!exporting} className={exportBtn}>
              <ImageIcon className="h-5 w-5 text-orange-500" />
              <span className="font-semibold text-sm">Report image (.png) — for WhatsApp</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
